/**
 * TQ #7975 (C1 Unified-Cockpit Tenant-Switcher):
 * Dropdown in der Sidebar-org-info-Zone das Personal + alle Org-Memberships
 * des Users anzeigt. Klick auf eine andere Org navigiert zum jeweiligen Cockpit.
 */

import { useState, useEffect, useRef } from 'react'
import { api, type TenantItem } from '../services/api'

interface TenantSwitcherProps {
  /** Aktueller Slug oder "personal" — wird als ausgewählt markiert. */
  currentSlug: string
}

export function TenantSwitcher({ currentSlug }: TenantSwitcherProps) {
  const [open, setOpen] = useState(false)
  const [tenants, setTenants] = useState<TenantItem[]>([])
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Laden beim ersten Öffnen
  useEffect(() => {
    if (!open || tenants.length > 0) return
    setLoading(true)
    api.getMyTenants()
      .then(res => setTenants(res.tenants ?? []))
      .catch(() => setTenants([]))
      .finally(() => setLoading(false))
  }, [open, tenants.length])

  // Schließen beim Klick außerhalb
  useEffect(() => {
    if (!open) return
    function handleOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [open])

  const otherTenants = tenants.filter(t =>
    t.kind === 'org' ? t.slug !== currentSlug : currentSlug !== 'personal',
  )
  if (otherTenants.length === 0 && !loading && tenants.length <= 1) {
    // Nur ein Tenant — kein Switcher nötig
    return null
  }

  return (
    <div className="tenant-switcher" ref={containerRef}>
      <button
        className="tenant-switcher-toggle"
        onClick={() => setOpen(v => !v)}
        title="Tenant wechseln"
        aria-label="Tenant wechseln"
      >
        ⇄
      </button>

      {open && (
        <div className="tenant-switcher-dropdown">
          <div className="tenant-switcher-title">Tenants wechseln</div>
          {loading && <div className="tenant-switcher-loading">Laden…</div>}
          {tenants.map(t => {
            const isCurrent = t.kind === 'personal'
              ? currentSlug === 'personal'
              : t.slug === currentSlug
            return (
              <a
                key={t.kind === 'personal' ? 'personal' : t.slug ?? t.public_token}
                href={isCurrent ? undefined : t.cockpit_url}
                className={`tenant-item${isCurrent ? ' tenant-item--current' : ''}`}
                onClick={isCurrent ? e => e.preventDefault() : undefined}
              >
                <span className="tenant-item-avatar">
                  {t.name.charAt(0).toUpperCase()}
                </span>
                <span className="tenant-item-info">
                  <span className="tenant-item-name">{t.name}</span>
                  <span className="tenant-item-role">
                    {t.kind === 'personal' ? 'Persönlich' : roleLabel(t.role)}
                  </span>
                </span>
                {isCurrent && <span className="tenant-item-check">✓</span>}
              </a>
            )
          })}
        </div>
      )}
    </div>
  )
}

function roleLabel(role: string): string {
  switch (role) {
    case 'owner': return 'Inhaber'
    case 'admin': return 'Administrator'
    case 'member': return 'Mitglied'
    default: return role
  }
}
